import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import Tooltip from './Tooltip';
import ChartModal from './ChartModal';

// Register the zoom plugin
Chart.register(zoomPlugin);

const CompactChart = ({ chartConfig, title, height = "h-64", tooltip }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [datasetVisibility, setDatasetVisibility] = useState([]);
    
    // Generate unique ID for this chart instance
    const chartId = useRef(`chart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`).current;

    // Create chart with proper timing
    useEffect(() => {
        // Destroy existing chart first
        if (chartInstance.current) {
            chartInstance.current.destroy();
            chartInstance.current = null;
        }

        // Wait for DOM to be ready
        if (!chartRef.current || !chartConfig) return;

        // Use setTimeout to ensure DOM is fully rendered
        const timeout = setTimeout(() => {
            if (chartRef.current && !chartInstance.current) {
                // Create a compact version with reduced padding
                const compactConfig = {
                    ...chartConfig,
                    options: {
                        ...chartConfig.options,
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: {
                            padding: {
                                top: 10,
                                right: 10,
                                bottom: 10,
                                left: 10
                            }
                        },
                        plugins: {
                            ...chartConfig.options.plugins,
                            legend: {
                                ...chartConfig.options.plugins?.legend,
                                labels: {
                                    ...chartConfig.options.plugins?.legend?.labels,
                                    boxWidth: 8,
                                    padding: 8,
                                    font: {
                                        size: 11
                                    }
                                }
                            },
                            title: {
                                ...chartConfig.options.plugins?.title,
                                display: false // Hide title in compact view
                            }
                        },
                        // Only add scales for charts that need them (not pie/doughnut)
                        ...(chartConfig.type !== 'pie' && chartConfig.type !== 'doughnut' && {
                            scales: {
                                ...chartConfig.options.scales,
                                x: {
                                    ...chartConfig.options.scales?.x,
                                    ticks: {
                                        ...chartConfig.options.scales?.x?.ticks,
                                        font: {
                                            size: 10
                                        }
                                    }
                                },
                                y: {
                                    ...chartConfig.options.scales?.y,
                                    ticks: {
                                        ...chartConfig.options.scales?.y?.ticks,
                                        font: {
                                            size: 10
                                        }
                                    }
                                }
                            }
                        })
                    }
                };

                try {
                    chartInstance.current = new Chart(chartRef.current, compactConfig);
                } catch (error) {
                    console.error('Error creating compact chart:', error);
                }
            }
        }, 100);

        return () => clearTimeout(timeout);
    }, [chartConfig]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    // Handle window resize
    useEffect(() => {
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (chartInstance.current) {
                    chartInstance.current.resize();
                }
            }, 100);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, []);

    const handleExpandClick = () => {
        // Capture current dataset visibility state before expanding
        if (chartInstance.current) {
            const visibilityState = chartInstance.current.data.datasets.map((dataset, index) => 
                chartInstance.current.isDatasetVisible(index)
            );
            setDatasetVisibility(visibilityState);
        }
        setShowFullScreen(true);
    };

    const handleCloseFullScreen = () => {
        setShowFullScreen(false);
    };

    // Callback to update compact chart when modal state changes (e.g., reset)
    const handleModalStateChange = (newVisibilityState) => {
        if (chartInstance.current && newVisibilityState.length > 0) {
            newVisibilityState.forEach((isVisible, index) => {
                if (index < chartInstance.current.data.datasets.length) {
                    chartInstance.current.setDatasetVisibility(index, isVisible);
                }
            });
            chartInstance.current.update();
            setDatasetVisibility(newVisibilityState);
        }
    };

    // Reset function for compact chart
    const handleCompactReset = () => {
        if (chartInstance.current) {
            // Show all datasets (sales reps)
            const resetVisibilityState = chartInstance.current.data.datasets.map(() => true);
            chartInstance.current.data.datasets.forEach((dataset, index) => {
                chartInstance.current.setDatasetVisibility(index, true);
            });
            
            // Reset zoom and pan controls if zoom plugin is available
            if (chartInstance.current.resetZoom) {
                chartInstance.current.resetZoom();
            }
            
            chartInstance.current.update();
            setDatasetVisibility(resetVisibilityState);
        }
    };

    return (
        <>
            {/* Compact Chart */}
            <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
                        <div className="flex items-center gap-2">
                            {tooltip && (
                                <Tooltip text={tooltip}>
                                    <div className="text-gray-400 hover:text-gray-600 transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </Tooltip>
                            )}
                            <Tooltip text="Reset chart view and show all data">
                                <button
                                    onClick={handleCompactReset}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </Tooltip>
                            <Tooltip text="Expand to full screen view">
                                <button
                                    onClick={handleExpandClick}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                    </svg>
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                    
                    <div 
                        className={`relative ${height} group`}
                    >
                        <canvas 
                            ref={chartRef}
                            id={chartId}
                            style={{ 
                                display: 'block',
                                width: '100%', 
                                height: '100%'
                            }}
                        ></canvas>
                    </div>
                </div>
            </div>

            {/* Full Screen Modal */}
            <ChartModal
                isOpen={showFullScreen}
                onClose={handleCloseFullScreen}
                chartConfig={chartConfig}
                title={title}
                initialDatasetVisibility={datasetVisibility}
                onVisibilityChange={handleModalStateChange}
            />
        </>
    );
};

export default CompactChart;